import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import CaseStudyTemplate from './CaseStudyTemplate';
import { fetchCaseStudyBySlug } from '../services/airtable';

export default function DynamicCaseStudy() {
  const { slug } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCaseStudy = async () => {
      try {
        setLoading(true);
        console.log('Fetching case study with slug:', slug);
        const data = await fetchCaseStudyBySlug(slug);
        console.log('Airtable data received:', data);
        setCaseStudy(data);
      } catch (err) {
        console.error('Error loading case study:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCaseStudy();
  }, [slug]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontSize: '1.2rem'
      }}>
        Loading case study...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontSize: '1.2rem',
        color: 'red'
      }}>
        Error: {error}
      </div>
    );
  }

  if (!caseStudy) {
    return <Navigate to="/case-studies" replace />;
  }

  // Convert text content to JSX elements with formatting support
  const renderTextContent = (text) => {
    // Normalize arrays (e.g., linked records) to a displayable string
    if (Array.isArray(text)) {
      text = text.join('\n\n');
    }
    if (!text) return <p>Content coming soon...</p>;
    
    // Split by double line breaks to preserve paragraph spacing from Airtable
    const sections = text.split(/\n\s*\n/);
    
    const parseFormattedText = (textContent) => {
      if (!textContent) return textContent;
      
      console.log('Original text content:', JSON.stringify(textContent));
      
      // Clean up text - replace various bold marker formats
      let cleanedText = textContent;
      
      // Handle various bold marker formats that might come from Airtable
      cleanedText = cleanedText.replace(/\|\*\|\*/g, '**');  // Convert |*|* to **
      cleanedText = cleanedText.replace(/\\\*\\\*/g, '**');  // Convert \*\* to **
      
      console.log('Cleaned text content:', JSON.stringify(cleanedText));
      
      // Split text by bold markers
      const parts = cleanedText.split(/(\*\*.*?\*\*)/g);
      
      return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          // Remove the ** markers and make bold
          const boldText = part.slice(2, -2);
          return <strong key={index}>{boldText}</strong>;
        }
        return part;
      });
    };
    
    return sections.map((section, sectionIndex) => {
      const trimmedSection = section.trim();
      if (!trimmedSection) return null;
      
      // Check if this section contains bullet points
      const lines = trimmedSection.split('\n');
      const hasBullets = lines.some(line => 
        line.trim().match(/^[-•*]\s+/) || // Dash, bullet, or asterisk bullets
        line.trim().match(/^\d+\.\s+/)    // Numbered lists
      );
      
      if (hasBullets) {
        // Separate regular text from bullet points
        const regularLines = [];
        const bulletLines = [];
        let currentMode = 'regular';
        
        lines.forEach(line => {
          const trimmedLine = line.trim();
          if (trimmedLine.match(/^[-•*]\s+/) || trimmedLine.match(/^\d+\.\s+/)) {
            currentMode = 'bullets';
          }
          
          if (currentMode === 'bullets' && (trimmedLine.match(/^[-•*]\s+/) || trimmedLine.match(/^\d+\.\s+/))) {
            bulletLines.push(trimmedLine);
          } else if (currentMode === 'regular' && trimmedLine) {
            regularLines.push(trimmedLine);
          }
        });
        
        return (
          <div key={sectionIndex}>
            {regularLines.length > 0 && (
              <p>{parseFormattedText(regularLines.join(' '))}</p>
            )}
            {bulletLines.length > 0 && (
              <ul>
                {bulletLines.map((bullet, bulletIndex) => {
                  const bulletText = bullet.replace(/^[-•*]\s+/, '').replace(/^\d+\.\s+/, '');
                  return (
                    <li key={bulletIndex}>
                      {parseFormattedText(bulletText)}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      } else {
        // Regular paragraph with formatting support
        return <p key={sectionIndex}>{parseFormattedText(trimmedSection)}</p>;
      }
    }).filter(Boolean); // Remove null entries
  };

  return (
    <CaseStudyTemplate
      clientName={caseStudy.clientName}
      title={caseStudy.title}
      subtitle={caseStudy.subtitle}
      heroImage={caseStudy.heroImageUrl}
      heroImageAlt={`${caseStudy.clientName} ${caseStudy.title}`}
      galleryImages={caseStudy.galleryImages}
      summary={renderTextContent(caseStudy.summary)}
      challenge={renderTextContent(caseStudy.challenge)}
      roleAndApproach={renderTextContent(caseStudy.roleAndApproach)}
      outcomes={renderTextContent(caseStudy.outcomes)}
      keyDeliverables={renderTextContent(caseStudy.keyDeliverables)}
      quotes={renderTextContent(caseStudy.quotes)}
      lessonsLearned={renderTextContent(caseStudy.lessonsLearned)}
    />
  );
}